/*
 * Purpose: AutoBlocks AI Director trail — expansion, journey tube, branded block ideas
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Route, Zap, Lightbulb } from 'lucide-react';
import type { DirectorExpansion } from '@/services/autoblocks/types';
import type { BrandedBlockSuggestion } from '@/lib/brandedBlocks';
import { BRANDED_IDEA_HELP } from '@/lib/brandedBlocks';
import { PRODUCT_BRAND } from '@/lib/productTerms';

interface DirectorTrailProps {
  director: DirectorExpansion | null | undefined;
  brandedIdeas?: BrandedBlockSuggestion[];
  isWorking?: boolean;
}

export function DirectorTrail({ director, brandedIdeas, isWorking }: DirectorTrailProps) {
  if (!director && !isWorking && !brandedIdeas?.length) return null;

  const isJourney = director?.pacing === 'journey_tube';

  return (
    <Card className="p-4 border-primary/25 bg-primary/5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary shrink-0" />
        <p className="text-sm font-medium">{PRODUCT_BRAND} AI Director</p>
        {isWorking ? (
          <Badge variant="secondary" className="text-xs">
            Expanding your request…
          </Badge>
        ) : isJourney ? (
          <Badge variant="outline" className="text-xs border-primary/40">
            <Route className="h-3 w-3 mr-1 inline" />
            Journey Tube
          </Badge>
        ) : director ? (
          <Badge variant="outline" className="text-xs">
            <Zap className="h-3 w-3 mr-1 inline" />
            Wiring blocks now
          </Badge>
        ) : null}
      </div>

      {isWorking && (
        <p className="text-xs text-muted-foreground">
          Reading your outcome, choosing pacing, and connecting blocks on the Mission map…
        </p>
      )}

      {director && !isWorking && (
        <>
          <p className="text-sm text-foreground">{director.directorNote}</p>
          <p className="text-xs text-muted-foreground">{director.rationale}</p>
          {isJourney && director.journeyPhases && director.journeyPhases.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
              {director.journeyPhases.map((phase) => (
                <li key={phase}>{phase}</li>
              ))}
            </ul>
          )}
        </>
      )}

      {brandedIdeas && brandedIdeas.length > 0 && (
        <div className="pt-2 border-t border-primary/15 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
            Branded block ideas (not built yet)
          </div>
          <p className="text-[11px] text-muted-foreground">{BRANDED_IDEA_HELP}</p>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            {brandedIdeas.map((idea) => (
              <li key={idea.subtype}>
                <span className="font-medium text-foreground">{idea.brand}</span> — {idea.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
